import Icon from 'components/Icon/Icon';
import {StrictModeDroppable} from 'components/StrictModeDroppable/StrictModeDroppable';
import Text from 'components/Text/Text';
import getItemStyle from 'features/beersManager/helpers/getItemStyle';
import reorder from 'features/beersManager/helpers/reorder';
import {changePubBeerPositionACS} from 'features/beersManager/requestAC';
import TableRowWrapper from 'features/beersManager/shared/TableRowWrapper/TableRowWrapper';
import {setPubBeersAC} from 'features/beersManager/slice';
import ZeroState from 'features/beersManager/Table/ZeroState/ZeroState';
import {useGlobalState} from 'hooks/useGlobalState';
import {useSetGlobalState} from 'hooks/useSetGlobalState';
import {DragDropContext, Draggable, DropResult} from 'react-beautiful-dnd';
import {PubBeer} from 'typescript/models/PubBeer';

import './ListBeersBody.scss';
import ListBeersRow from './ListBeersRow/ListBeersRow';

interface IProps {
  beersList: PubBeer[];
}

const ListBeersBody = ({beersList}: IProps) => {
  const dispatch = useSetGlobalState();

  const {managedPub} = useGlobalState((state) => state.managedPub);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !managedPub) return;

    const {draggableId, destination, source} = result;

    const isPositionChanged = source.index !== destination.index;
    const isDraggedBeetweenBeersOnTapOnly = beersList[destination.index].attributes.on_tap;

    if (!isPositionChanged || !isDraggedBeetweenBeersOnTapOnly) return;

    const reorderedArray = reorder({list: beersList, startIndex: source.index, endIndex: destination.index});

    dispatch(setPubBeersAC(reorderedArray));

    dispatch(
      changePubBeerPositionACS({
        pubId: managedPub.id,
        beerId: draggableId,
        position: destination.index,
      }),
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable droppableId="list-beers">
        {(provided) => (
          <tbody {...provided.droppableProps} ref={provided.innerRef} data-testid="list-beers-body">
            {beersList.length ? (
              beersList.map((beer, index) => (
                <Draggable key={beer.id} draggableId={beer.id} index={index} isDragDisabled={!beer.attributes.on_tap}>
                  {(provided, snapshot) => (
                    <TableRowWrapper
                      className={`list-beers-row ${beer.attributes.on_tap && 'list-beers-row--on-tap'}`}
                      data-testid="list-beers-row"
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                      <ListBeersRow beer={beer} />
                    </TableRowWrapper>
                  )}
                </Draggable>
              ))
            ) : (
              <ZeroState>
                <div data-testid="list-beers-body-zero-state" className="list-beers-body__zero-state">
                  <div className="list-beers-body__zero-state-icon">
                    <Icon icon="empty-tap" />
                  </div>
                  <Text tag="h3">
                    You don’t have any beer on tap. <br /> Find and add new beers in the list above.
                  </Text>
                </div>
              </ZeroState>
            )}
            {provided.placeholder}
          </tbody>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

export default ListBeersBody;
